import moment from 'moment';
import { snakeCase } from 'lodash';

import { __t, applyTranslations } from '../../language';

import { GRAPH_COLORS } from '../utils';
import { abbrNum } from 'utils/helper/common';
import { CHANNEL_TYPE_YOUTUBE } from 'utils/channels';

import type { BarGraphDateInterval, GraphOverTimeProfileItem } from 'types';

applyTranslations(require('./profile-graphs.i18n.json'));

const FOLLOWERS = 'followers';
const ENGAGEMENT_RATE = 'engagementRate';
const VIDEO_VIEWS = 'videoViews';

const WEEK: BarGraphDateInterval = 'week';
const MONTH: BarGraphDateInterval = 'month';
const YEAR: BarGraphDateInterval = 'year';

export const INTERVAL = {
  WEEK,
  MONTH,
  YEAR
};

const getData = (data: Array<GraphOverTimeProfileItem>, prop: string): Array<number> => data.map(item => (
  prop === ENGAGEMENT_RATE ? item[prop] * 100 : item[prop]
));

const getXAxisOptions = (data: any, dateInterval: string): unknown => ({
  data: data.map(item => moment(item.date).format('YYYY-MM-DD')),
  type: 'category',
  boundaryGap: false,

  nameTextStyle: {
    color: '#3a3a3a'
  },

  axisTick: {
    alignWithLabel: true,
    length: 11
  },

  axisLine: {
    onZero: false,
    lineStyle: {
      color: '#e0e0e0'
    }
  },

  axisLabel: {
    color: '#3a3a3a',
    margin: 13,
    formatter: (value: string): string => {
      if (dateInterval === MONTH || dateInterval === WEEK) {
        return moment(value).format('Do MMM');
      }

      // 1+ years
      return moment(value).format('MMM YYYY');
    }
  }
});

const getYAxisOptions = (yAxisMin: number, yAxisMax: number, dataType: string): any => {
  const diff = yAxisMax - yAxisMin;
  const paddingPercentage = 0.2;
  const paddedMin = Math.max(0, yAxisMin - (paddingPercentage * diff));
  const paddedMax = yAxisMax + (paddingPercentage * diff);

  return {
    type: 'value',
    min: paddedMin,
    max: paddedMax,
    nameGap: 10,
    nameTextStyle: {
      color: '#3a3a3a'
    },
    axisTick: {
      alignWithLabel: true,
      length: 11
    },
    axisLine: {
      lineStyle: {
        color: '#e0e0e0'
      }
    },
    axisLabel: {
      formatter: (value: number): string => (dataType === ENGAGEMENT_RATE ? `${value.toFixed(2)}%` : abbrNum(value, {}, true)),
      color: '#3a3a3a',
      margin: 13,
      showMaxLabel: false
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: '#F1F3F5',
        type: 'solid'
      }
    }
  };
};

const getSeriesOptions = (data: any): Array<any> => [
  {
    data,
    type: 'line',
    symbol: 'circle',
    symbolSize: data && (data.length === 1) ? 10 : 1, // For single data point keeping symbol size large for visibility
    smooth: true
  }
];

const getUpdatedTooltipValue = (value: number, dataType: string): string => {
  if (dataType === FOLLOWERS || dataType === VIDEO_VIEWS) {
    return abbrNum(value, {}, true);
  }

  if (dataType === ENGAGEMENT_RATE) {
    return typeof value === 'number' ? `${value.toFixed(2)}%` : value;
  }

  return ''; // should not happen
};

const getTooltip = (param: any, data: Array<any>, dataType: string, channel: string): string => {
  const { dataIndex, value } = param;
  const updatedValue = getUpdatedTooltipValue(value, dataType);
  const styles = 'margin: 2px 0; color: #fff;';
  let label = __t(`profile_graphs.over_time.label_${snakeCase(dataType)}`);

  if (dataType === FOLLOWERS && channel === CHANNEL_TYPE_YOUTUBE) {
    label = __t('profile_graphs.over_time.label_subscribers');
  }

  return `
    <div style="${styles}">
      ${moment(data[dataIndex].date).format('DD/MM/YYYY')}
    </div>
    <div style="${styles}">
      ${label}:
      ${updatedValue}
    </div>
  `;
};

export const getProfileGraphOverTimeOptions = (
  data: Array<GraphOverTimeProfileItem>,
  dataType: string,
  dateInterval: string,
  channel: string
): any | undefined | null => {
  const hasGraphData = data.some(item => Boolean(item[dataType]));

  if (!hasGraphData) {
    return null;
  }

  const updatedData = getData(data, dataType);

  if (updatedData.length < 3 && dataType !== 'videoViews') {
    return null;
  }

  const yAxisMin = Math.min(...updatedData);
  const yAxisMax = Math.max(...updatedData);

  const grid = {
    width: 1072,
    top: 30,
    left: 56,
    right: 40
  };

  const legend = {
    show: false
  };

  const tooltip = {
    trigger: 'axis',
    position: (mousePosition: Array<number>): Array<number> => {
      const x = mousePosition[0];
      const xPos = x < 960 ? x + 10 : x - 124;

      return [xPos, mousePosition[1] + 10];
    },
    formatter: (params: any): string => getTooltip(params[0], data, dataType, channel),
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: 10,
    axisPointer: {
      type: 'cross',
      label: {
        show: true,
        backgroundColor: '#3a3a3a',
        formatter: (params: any) => {
          if (params.axisDimension === 'x') {
            if (dateInterval === MONTH || dateInterval === WEEK) {
              return moment(params.value).format('Do MMM');
            }

            // 1+ years
            return moment(params.value).format('MMM YYYY');
          }

          return getUpdatedTooltipValue(params.value, dataType);
        }
      }
    },
    extraCssText: 'border-radius: 8px;'
  };

  const xAxisBottom = getXAxisOptions(data, dateInterval);

  return {
    color: GRAPH_COLORS,
    grid,
    legend,
    tooltip,
    series: getSeriesOptions(updatedData),
    xAxis: [
      xAxisBottom
    ],
    yAxis: getYAxisOptions(yAxisMin, yAxisMax, dataType)
  };
};
